import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ArrowUpRight, Paintbrush, Home as HomeIcon, Layers } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BUSINESS, IMAGES, SERVICES, CITIES, HOME_FAQ, PROCESS_STEPS } from "@/siteData";
import { trackEvent } from "@/lib/analytics";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HERO_LINES = [
  { text: "JC Colors", accent: false },
  { text: "Drywall & Painting", accent: true },
];

const SERVICE_ICONS = { "interior-painting": Paintbrush, "exterior-painting": HomeIcon, "drywall-repair": Layers };

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <>
      <Seo
        title="JC Colors Remodeling LLC | Painters & Drywall in Kansas City"
        description="Looking for painting or drywall services in Overland Park, Kansas City, Lenexa, or Olathe? Contact JC Colors Remodeling LLC to discuss your next project."
        jsonLd={faqJsonLd}
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="home-hero">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-28 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700"
            >
              Painting & Drywall — Overland Park · KCMO · Lenexa · Olathe
            </motion.p>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "text-amber-600" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.85, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              JC Colors Remodeling LLC is a residential painting and drywall
              contractor serving Overland Park, Kansas City, Lenexa and Olathe.
              Interior rooms, full exteriors, drywall repair and finishing —
              every project starts with a free estimate and ends with a
              walkthrough.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-get-estimate-button"
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-all duration-200 hover:scale-105 hover:bg-amber-700"
              >
                Get a Free Estimate
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-phone-button"
                onClick={() => trackEvent("phone_click", { location: "hero" })}
                className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-7 py-3 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
              >
                <Phone className="h-4 w-4" />
                {BUSINESS.phone}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-10 flex flex-wrap gap-2.5"
            >
              {["Free on-site estimates", "Interior · Exterior · Drywall", "Locally owned, KC metro proud"].map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-semibold text-slate-600 backdrop-blur-sm"
                >
                  {chip}
                </span>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -left-4 -top-4 h-full w-full rounded-[2rem] border-2 border-amber-600/40" />
              <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-slate-900/20">
                <motion.img
                  src={IMAGES.heroExterior}
                  alt="Exterior painting project by JC Colors Remodeling — windows masked and siding prepped on a Kansas City area home"
                  className="h-[420px] w-full object-cover lg:h-[520px]"
                  style={{ y: imgY, scale: imgScale }}
                  loading="eager"
                  fetchpriority="high"
                />
                <div className="absolute bottom-4 left-4 rounded-2xl bg-white/90 px-5 py-3 backdrop-blur-md">
                  <p className="font-display text-sm font-bold text-slate-900">Painting & Drywall</p>
                  <p className="text-xs text-slate-600">Kansas City metropolitan area</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="home-services-section">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">What we do</p>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Painting services for every surface of your home
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.slug];
            const wide = i === 0;
            return (
              <Reveal key={s.slug} delay={i * 0.1} className={wide ? "md:col-span-7" : "md:col-span-5"}>
                <Link
                  to={`/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group relative block h-full overflow-hidden rounded-3xl bg-slate-900"
                >
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    loading="lazy"
                    className={`w-full object-cover opacity-90 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100 ${wide ? "h-72 sm:h-96" : "h-72"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Icon className="h-3.5 w-3.5" />
                      {s.title}
                    </span>
                    <h3 className="mt-3 font-display text-xl font-bold text-white sm:text-2xl">{s.title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-200">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400">
                      Explore {s.title.toLowerCase()}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
          <Reveal delay={0.2} className="md:col-span-12">
            <Link
              to="/painting-services"
              data-testid="home-all-services-card"
              className="group flex flex-col items-start justify-between gap-6 rounded-3xl bg-amber-600 p-8 transition-colors duration-300 hover:bg-amber-700 sm:flex-row sm:items-center sm:p-10"
            >
              <div>
                <h3 className="font-display text-2xl font-bold text-white">See everything we paint</h3>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-amber-100">
                  From single-room refreshes to full exterior repaints — get the
                  full picture of our painting services across the KC metro.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-amber-700 transition-transform duration-200 group-hover:scale-105">
                All Painting Services
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-900 py-16 text-white sm:py-24" data-testid="home-process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-500">How it works</p>
            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              A finish worth looking at starts long before the paint
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <p className="font-mono-accent text-4xl font-medium text-amber-600">{step.n}</p>
                <h3 className="mt-4 font-display text-lg font-bold">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="home-service-areas-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">Where we work</p>
            <h2 className="mt-3 max-w-xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Painting & drywall across the Kansas City metro
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/service-areas"
              data-testid="home-view-all-areas-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-amber-700 transition-colors hover:text-amber-800"
            >
              View all service areas
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CITIES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.08}>
              <Link
                to={`/service-areas/${c.slug}`}
                data-testid={`home-city-card-${c.slug}`}
                className="group block h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-amber-600/50 hover:shadow-lg"
              >
                <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-amber-700">{c.state}</p>
                <h3 className="mt-2 font-display text-xl font-bold text-slate-900">{c.city}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Painters & drywall repair in {c.city}, {c.state} — free estimates, careful prep, clean finishes.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700">
                  {c.city} painting services
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-white/60 py-16 sm:py-24" data-testid="home-faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">Common questions</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Before you pick up a brush, pick up the phone
            </h2>
          </Reveal>
          <div className="mt-8 space-y-3">
            {HOME_FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <details className="group rounded-2xl border border-slate-200 bg-white p-5 transition-colors open:border-amber-600/40">
                  <summary
                    data-testid={`home-faq-question-${i}`}
                    className="cursor-pointer list-none font-display text-base font-semibold text-slate-900 marker:hidden"
                  >
                    <span className="flex items-center justify-between gap-4">
                      {f.q}
                      <span className="text-amber-600 transition-transform duration-200 group-open:rotate-45">+</span>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-slate-900" data-testid="home-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 py-16 sm:px-6 sm:py-20 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Ready when you are.
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Tell us about your painting or drywall project in Overland Park,
              Kansas City, Lenexa or Olathe — the estimate is free and the
              advice is honest.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="cta-get-estimate-button"
              className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-amber-700"
            >
              Get a Free Estimate
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="cta-call-phone-button"
              onClick={() => trackEvent("phone_click", { location: "cta-band" })}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-7 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-amber-500 hover:text-amber-400"
            >
              <Phone className="h-4 w-4" />
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
